import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

import { PostHogFeatureFlagMap } from 'src/context/flagsContext';

// W3C Trace Context header carried on every REST call to the Go backend so the
// backend adopts our trace id as its request id. Version 00, sampled flag set.
export const TRACEPARENT_HEADER = 'traceparent';
const TRACEPARENT_VERSION = '00';
const TRACEPARENT_FLAGS_SAMPLED = '01';
const TRACE_ID_BYTES = 16;
const PARENT_ID_BYTES = 8;
// Each API call gets its own child span carrying the trace id, so a browser
// transaction that fans out into several requests keeps every id instead of the
// last one overwriting the rest on the parent span.
const SENTRY_TRACE_SPAN_NAME = 'assembly.api_request';
const SENTRY_TRACE_SPAN_OP = 'assembly.trace';
const SENTRY_TRACE_ID_ATTRIBUTE = 'assembly.trace_id';

const randomHex = (bytes: number) => {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return Array.from(buffer, (b) => b.toString(16).padStart(2, '0')).join('');
};

export const generateTraceparent = () => {
  const traceId = randomHex(TRACE_ID_BYTES);
  const parentId = randomHex(PARENT_ID_BYTES);
  return `${TRACEPARENT_VERSION}-${traceId}-${parentId}-${TRACEPARENT_FLAGS_SAMPLED}`;
};

export const traceIdFromTraceparent = (traceparent: string) =>
  traceparent.split('-')[1];

// Read from the PostHog singleton rather than FlagsContext because the API
// helper is not a React component. The singleton is authoritative once it
// answers, but returns undefined until posthog-js finishes initializing, so
// requests fired before then fall back to the flags the server bootstrapped
// into window.App at page load.
export const isBrowserTraceIdsEnabled = () => {
  try {
    const flag = PostHogFeatureFlagMap.EnableBrowserTraceIds;
    const singletonValue = posthog.isFeatureEnabled(flag);
    if (singletonValue !== undefined) {
      return singletonValue === true;
    }
    if (typeof window === 'undefined') {
      return false;
    }
    return window.App?.posthogBootstrap?.featureFlags?.[flag] === true;
  } catch {
    return false;
  }
};

/**
 * Returns headers with a fresh traceparent attached when the flag is on, and
 * records the trace id on a child span of Sentry's active transaction so the
 * browser transaction and the backend request share a queryable id.
 */
export const withTraceparent = (headers: Record<string, unknown>) => {
  if (!isBrowserTraceIdsEnabled()) {
    return headers;
  }
  const traceparent = generateTraceparent();
  Sentry.startInactiveSpan({
    name: SENTRY_TRACE_SPAN_NAME,
    op: SENTRY_TRACE_SPAN_OP,
    onlyIfParent: true,
    attributes: {
      [SENTRY_TRACE_ID_ATTRIBUTE]: traceIdFromTraceparent(traceparent),
    },
  }).end();
  return { ...headers, [TRACEPARENT_HEADER]: traceparent };
};
